import { useState } from "react";
import type { CloudAllData } from "@/types/cloud-data";
import { SubTabPanel } from "./TabPanel";
import { EditModal } from "./EditModal";
import { getItemName } from "@/data/item-names";

const INVENTORY_TABS = [
  { id: "equipments", label: "Equipments", key: "equipments" },
  { id: "materials", label: "Materials", key: "materials" },
  { id: "consumables", label: "Consumables", key: "consumables" },
  { id: "runeItems", label: "Rune Items", key: "runeItems" },
  { id: "keyItems", label: "Key Items", key: "keyItems" },
  { id: "storagedEquipments", label: "Stored", key: "storagedEquipments" },
] as const;

type InvTabId = (typeof INVENTORY_TABS)[number]["id"];

interface InventoryTabProps {
  cloudData: CloudAllData | null;
  onSaveCloudData: (newCloudData: CloudAllData) => void;
}

interface ItemRow {
  id: string | null;
  enhancementLevel: number;
  currentRarity: number;
  quantity: number;
  randomStat?: { stats: number; value: number } | null;
  fixedStats?: { stats: number; value: number }[];
  socketStats?: { socketID: string; currentLevel: number; currentEXP?: number }[];
  currentEXP?: number;
  [key: string]: unknown;
}

const RARITY_COLORS: Record<number, string> = {
  0: "#FFFFFF",
  1: "#9EFF9E",
  2: "#66B2FF",
  3: "#FF786E",
  4: "#FF8CFF",
};

const EQUIP_TYPES = new Set(["equipments", "storagedEquipments"]);

const STAT_ABBR: Record<number, string> = {
  0:  "—",
  1:  "ATK",
  2:  "DEF",
  3:  "ATK SPD",
  4:  "CRIT",
  5:  "CRIT DMG",
  6:  "HP%",
  7:  "REC",
  8:  "ELEM RES",
  9:  "PHY RES",
  10: "EVA",
  11: "SPC",
  12: "MPR",
  13: "CDR",
  14: "MAX HP",
  15: "MAX MP",
  16: "B.ATK",
  17: "S.ATK",
  18: "ACC",
  19: "MANA GN",
  20: "SP REGEN",
  21: "CON+",
  22: "ATK%",
  23: "DEF%",
};

function itemColor(rarity: number): string {
  return RARITY_COLORS[rarity] ?? "#FFFFFF";
}

function formatStats(item: ItemRow, includeSocket: boolean): string {
  const parts: string[] = [];
  if (item.randomStat) {
    const abbr = STAT_ABBR[item.randomStat.stats] ?? `S${item.randomStat.stats}`;
    parts.push(`${abbr}:${(item.randomStat.value / 10).toFixed(1)}%`);
  }
  if (item.fixedStats?.length) {
    const sorted = [...item.fixedStats].sort((a, b) => a.stats - b.stats);
    for (const f of sorted) {
      const abbr = STAT_ABBR[f.stats] ?? `S${f.stats}`;
      parts.push(`${abbr}:${(f.value / 10).toFixed(1)}%`);
    }
  }
  if (includeSocket && item.socketStats?.length) {
    for (const s of item.socketStats) {
      const rName = getItemName(s.socketID);
      parts.push(rName ? `${rName}+${s.currentLevel}` : `Rune:${s.socketID}+${s.currentLevel}`);
    }
  }
  return parts.join(" | ") || "—";
}

function ItemName({ item, isEquip }: { item: ItemRow; isEquip: boolean }) {
  const name = getItemName(item.id);
  if (isEquip) {
    const color = itemColor(item.currentRarity ?? 0);
    const label = item.enhancementLevel > 0 ? `${name} +${item.enhancementLevel}` : name;
    return <span style={{ color }}>{label}</span>;
  }
  return <span>{name}</span>;
}

function EquipmentTable({ items }: { items: unknown }) {
  if (!items) return <div className="text-sm text-muted-foreground">—</div>;
  if (typeof items === "string") {
    try { items = JSON.parse(items); } catch { /* passthrough */ }
  }
  if (!Array.isArray(items)) return <div className="text-sm text-muted-foreground">Not an array</div>;
  if (items.length === 0) return <div className="text-sm text-muted-foreground">Empty</div>;

  const validRows = (items as ItemRow[]).filter(r => r.id);
  return (
    <div className="rounded border border-border overflow-x-auto">
      <table className="w-full text-sm table-fixed">
        <thead>
          <tr className="bg-muted/50 text-xs text-muted-foreground border-b border-border">
            <th className="px-3 py-2 text-left w-10 shrink-0">#</th>
            <th className="px-3 py-2 text-left w-48 shrink-0">Item</th>
            <th className="px-3 py-2 text-left">Status</th>
          </tr>
        </thead>
        <tbody>
          {validRows.map((item, i) => (
            <tr key={i} className="border-t border-border/50 hover:bg-muted/30">
              <td className="px-3 py-2 text-xs text-muted-foreground w-10 shrink-0">{i + 1}</td>
              <td className="px-3 py-2 text-sm font-medium truncate w-48 shrink-0"><ItemName item={item} isEquip={true} /></td>
              <td className="px-3 py-2 text-xs font-mono text-muted-foreground truncate">{formatStats(item, true)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RuneTable({ items }: { items: unknown }) {
  if (!items) return <div className="text-sm text-muted-foreground">—</div>;
  if (typeof items === "string") {
    try { items = JSON.parse(items); } catch { /* passthrough */ }
  }
  if (!Array.isArray(items)) return <div className="text-sm text-muted-foreground">Not an array</div>;
  if (items.length === 0) return <div className="text-sm text-muted-foreground">Empty</div>;

  const validRows = (items as ItemRow[]).filter(r => r.id);
  return (
    <div className="rounded border border-border overflow-x-auto">
      <table className="w-full text-sm table-fixed">
        <thead>
          <tr className="bg-muted/50 text-xs text-muted-foreground border-b border-border">
            <th className="px-3 py-2 text-left w-10 shrink-0">#</th>
            <th className="px-3 py-2 text-left w-48 shrink-0">Item</th>
            <th className="px-3 py-2 text-left">Status</th>
          </tr>
        </thead>
        <tbody>
          {validRows.map((item, i) => (
            <tr key={i} className="border-t border-border/50 hover:bg-muted/30">
              <td className="px-3 py-2 text-xs text-muted-foreground w-10 shrink-0">{i + 1}</td>
              <td className="px-3 py-2 text-sm font-medium truncate w-48 shrink-0"><ItemName item={item} isEquip={false} /></td>
              <td className="px-3 py-2 text-xs font-mono text-muted-foreground truncate">{formatStats(item, false)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SimpleItemTable({ items }: { items: unknown }) {
  if (!items) return <div className="text-sm text-muted-foreground">—</div>;
  if (typeof items === "string") {
    try { items = JSON.parse(items); } catch { /* passthrough */ }
  }
  if (!Array.isArray(items)) return <div className="text-sm text-muted-foreground">Not an array</div>;
  if (items.length === 0) return <div className="text-sm text-muted-foreground">Empty</div>;

  const validRows = (items as ItemRow[]).filter(r => r.id);
  return (
    <div className="overflow-x-auto">
      <table className="text-sm table-fixed" style={{ minWidth: '100%' }}>
        <thead>
          <tr className="bg-muted/50 text-xs text-muted-foreground border-b border-border">
            <th className="px-3 py-2 text-left" style={{ width: '2.5rem', minWidth: '2.5rem' }}>#</th>
            <th className="px-3 py-2 text-left" style={{ width: '12rem', minWidth: '12rem' }}>Item</th>
            <th className="px-3 py-2 text-left">Qty</th>
          </tr>
        </thead>
        <tbody>
          {validRows.map((item, i) => (
            <tr key={i} className="border-t border-border/50 hover:bg-muted/30">
              <td className="px-3 py-2 text-xs text-muted-foreground" style={{ width: '2.5rem', minWidth: '2.5rem' }}>{i + 1}</td>
              <td className="px-3 py-2 text-sm font-medium truncate" style={{ width: '12rem', minWidth: '12rem' }}><ItemName item={item} isEquip={false} /></td>
              <td className="px-3 py-2 text-xs">{item.quantity ?? 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function InventoryTab({ cloudData, onSaveCloudData }: InventoryTabProps) {
  const [activeSub, setActiveSub] = useState<InvTabId>("equipments");
  const [editModal, setEditModal] = useState(false);

  const activeConfig = INVENTORY_TABS.find(t => t.id === activeSub)!;
  const items = (cloudData as unknown as Record<string, unknown>)?.[activeConfig.key] as unknown ?? null;
  const isEquip = EQUIP_TYPES.has(activeConfig.id);
  const isRune = activeConfig.id === "runeItems";

  return (
    <div className="space-y-3">
      <SubTabPanel
        tabs={INVENTORY_TABS.map(t => ({ id: t.id, label: t.label }))}
        activeTab={activeSub}
        onTabChange={(id) => setActiveSub(id as InvTabId)}
      />
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground font-mono">{activeConfig.key}</span>
        {Array.isArray(items) && (
          <span className="text-xs text-muted-foreground">({(items as ItemRow[]).filter(r => r.id).length} items)</span>
        )}
      </div>
      {isEquip ? (
        <EquipmentTable items={items} />
      ) : isRune ? (
        <RuneTable items={items} />
      ) : (
        <SimpleItemTable items={items} />
      )}
      <div className="flex justify-end">
        <button onClick={() => setEditModal(true)} className="text-xs text-primary hover:underline">Edit JSON</button>
      </div>
      {editModal && cloudData && (
        <EditModal
          keyName="EC2_CLOUD_ALL_DATA"
          initialValue={JSON.stringify(cloudData, null, 2)}
          onSave={(val) => {
            try { onSaveCloudData(JSON.parse(val)); } catch { /* ignore */ }
            setEditModal(false);
          }}
          onClose={() => setEditModal(false)}
        />
      )}
    </div>
  );
}